import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { CATEGORIES, localize } from '../data/mock';
import { fetchProperties } from '../data/api';
import PropertyCard from '../components/PropertyCard';
import Reveal from '../components/Reveal';
import { HERO_IMAGES } from '../data/mock';

const COUNTRIES = ['France', 'Suisse', 'International'];

const Properties = () => {
  const { t, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const catParam = params.get('cat') || 'all';
  const [country, setCountry] = useState('all');
  const [allProps, setAllProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [surfaceMin, setSurfaceMin] = useState('');
  const [surfaceMax, setSurfaceMax] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchProperties().then((data) => {
      if (active) {
        setAllProps(data);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [catParam]);

  const setCat = (slug) => {
    if (slug === 'all') {
      params.delete('cat');
    } else {
      params.set('cat', slug);
    }
    setParams(params, { replace: true });
  };

  const filtered = useMemo(() => {
    const pMin = priceMin ? parseFloat(priceMin) * 1000000 : null;
    const pMax = priceMax ? parseFloat(priceMax) * 1000000 : null;
    const sMin = surfaceMin ? parseFloat(surfaceMin) : null;
    const sMax = surfaceMax ? parseFloat(surfaceMax) : null;
    return allProps.filter((p) => {
      const okCat = catParam === 'all' || p.category === catParam;
      const okCountry = country === 'all' || p.country === country;
      const okPMin = pMin == null || (p.price != null && p.price >= pMin);
      const okPMax = pMax == null || (p.price != null && p.price <= pMax);
      const okSMin = sMin == null || (p.surface != null && p.surface >= sMin);
      const okSMax = sMax == null || (p.surface != null && p.surface <= sMax);
      return okCat && okCountry && okPMin && okPMax && okSMin && okSMax;
    });
  }, [allProps, catParam, country, priceMin, priceMax, surfaceMin, surfaceMax]);

  const resetAdvanced = () => {
    setPriceMin(''); setPriceMax(''); setSurfaceMin(''); setSurfaceMax('');
  };

  const activeCat = CATEGORIES.find((c) => c.slug === catParam);

  return (
    <div className="bg-[#f4efe6]">
      {/* header banner */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={activeCat ? activeCat.cover : HERO_IMAGES.parisGolden} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1b2331]/70" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="font-sans-elegant text-[0.7rem] tracking-brand text-[#c8a469] mb-5">
            {t('hero.regions')}
          </p>
          <h1 className="font-serif-display text-4xl md:text-6xl text-[#f4efe6] font-light leading-tight">
            {activeCat ? localize(activeCat.label, lang) : t('properties.title')}
          </h1>
          <p className="font-sans-elegant text-[#f4efe6]/70 max-w-xl mt-5 leading-relaxed">
            {activeCat ? localize(activeCat.desc, lang) : t('properties.subtitle')}
          </p>
        </div>
      </section>

      {/* filters */}
      <section className="sticky top-[68px] z-30 bg-[#f4efe6]/95 backdrop-blur-md border-b border-[#1b2331]/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <button
              onClick={() => setCat('all')}
              className={`font-sans-elegant text-[0.72rem] tracking-wide-2 uppercase transition-colors ${
                catParam === 'all' ? 'text-[#b0894f]' : 'text-[#1b2331]/60 hover:text-[#1b2331]'
              }`}
            >
              {t('properties.filterAll')}
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setCat(c.slug)}
                className={`font-sans-elegant text-[0.72rem] tracking-wide-2 uppercase transition-colors ${
                  catParam === c.slug ? 'text-[#b0894f]' : 'text-[#1b2331]/60 hover:text-[#1b2331]'
                }`}
              >
                {localize(c.label, lang)}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCountry('all')}
                className={`font-sans-elegant text-[0.7rem] tracking-wide-2 uppercase px-3 py-1.5 border transition-colors ${
                  country === 'all' ? 'border-[#b0894f] text-[#b0894f]' : 'border-[#1b2331]/15 text-[#1b2331]/60 hover:border-[#1b2331]/40'
                }`}
              >
                {t('properties.countryAll')}
              </button>
              {COUNTRIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCountry(c)}
                  className={`font-sans-elegant text-[0.7rem] tracking-wide-2 uppercase px-3 py-1.5 border transition-colors ${
                    country === c ? 'border-[#b0894f] text-[#b0894f]' : 'border-[#1b2331]/15 text-[#1b2331]/60 hover:border-[#1b2331]/40'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowAdvanced((s) => !s)}
              className={`ml-2 flex items-center gap-1.5 font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase transition-colors ${
                showAdvanced || priceMin || priceMax || surfaceMin || surfaceMax ? 'text-[#b0894f]' : 'text-[#1b2331]/60 hover:text-[#1b2331]'
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {t('properties.advanced')}
            </button>
          </div>
        </div>

        {/* advanced filters */}
        <div className={`overflow-hidden transition-all duration-400 ${showAdvanced ? 'max-h-52' : 'max-h-0'}`}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-6">
            <div className="flex flex-wrap items-end gap-x-8 gap-y-4 border-t border-[#1b2331]/10 pt-5">
              <div>
                <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">{t('properties.priceRange')}</p>
                <div className="flex items-center gap-2">
                  <input type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder={t('properties.min')} className="w-24 bg-transparent border-b border-[#1b2331]/25 py-1.5 font-sans-elegant text-sm text-[#1b2331] focus:outline-none focus:border-[#b0894f]" />
                  <span className="text-[#8a8d93]">—</span>
                  <input type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder={t('properties.max')} className="w-24 bg-transparent border-b border-[#1b2331]/25 py-1.5 font-sans-elegant text-sm text-[#1b2331] focus:outline-none focus:border-[#b0894f]" />
                </div>
              </div>
              <div>
                <p className="font-sans-elegant text-[0.62rem] tracking-wide-2 uppercase text-[#8a8d93] mb-2">{t('properties.surfaceRange')}</p>
                <div className="flex items-center gap-2">
                  <input type="number" value={surfaceMin} onChange={(e) => setSurfaceMin(e.target.value)} placeholder={t('properties.min')} className="w-24 bg-transparent border-b border-[#1b2331]/25 py-1.5 font-sans-elegant text-sm text-[#1b2331] focus:outline-none focus:border-[#b0894f]" />
                  <span className="text-[#8a8d93]">—</span>
                  <input type="number" value={surfaceMax} onChange={(e) => setSurfaceMax(e.target.value)} placeholder={t('properties.max')} className="w-24 bg-transparent border-b border-[#1b2331]/25 py-1.5 font-sans-elegant text-sm text-[#1b2331] focus:outline-none focus:border-[#b0894f]" />
                </div>
              </div>
              {(priceMin || priceMax || surfaceMin || surfaceMax) && (
                <button onClick={resetAdvanced} className="font-sans-elegant text-[0.68rem] tracking-wide-2 uppercase text-[#b0894f] link-underline">
                  {t('properties.reset')}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="font-sans-elegant text-xs tracking-wide-2 uppercase text-[#8a8d93] mb-10">
            {loading ? '…' : `${filtered.length} ${t('properties.results')}`}
          </p>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-[#e2d9c8]" />
                  <div className="h-3 w-24 bg-[#e2d9c8] mt-5" />
                  <div className="h-5 w-3/4 bg-[#e2d9c8] mt-3" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <p className="font-serif-display text-2xl text-[#1b2331]/50 py-20 text-center">
              {t('properties.empty')}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {filtered.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 80}>
                  <PropertyCard property={p} />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Properties;
